export type UserRole = 'SUPERADMIN' | 'OPERATOR' | 'STAFF' | 'RESIDENT';

export interface User {
  id: string;
  email: string;
  username?: string;
  name: string;
  phoneNumber?: string;
  role: UserRole; // 'STAFF' covers all staff, managers, etc.
  groupId?: string; // Permission group
  buildingId?: string; // Assigned building
  operatorId?: string; // For users under an operator
  createdAt: string;
  updatedAt: string;
}

export interface PermissionGroup {
  id: string;
  name: string; // e.g., "Quản trị hệ thống", "Nhân viên thu ngân"
  description?: string;
  permissions: Permission[]; // List of permissions for modules
  operatorId: string; // Created by which operator
}

export interface Permission {
  module: string; // e.g., "apartment", "building", "account", "payment"
  actions: ('view' | 'edit' | 'full')[];
}

export interface Building {
  id: string;
  name: string;
  address: string;
  operatorId: string;
  operator: User;
  staff: User[];
  apartments: Apartment[];
  createdAt: string;
  updatedAt: string;
}

export interface Apartment {
  id: string;
  code: string;
  customerCode?: string;
  ownerName: string;
  ownerPhone?: string;
  ownerEmail?: string;
  address?: string;
  area?: number;
  taxCode?: string;
  notes?: string;
  hasVatInvoice: boolean;
  status: string;
  buildingId: string;
  groupId?: string;
  building?: Building;
  group?: ApartmentGroup;
  residents: User[];
  bills: Bill[];
  createdAt: string;
  updatedAt: string;
}

export interface Bill {
  id: string;
  amount: number;
  dueDate: string;
  status: 'PENDING' | 'PAID' | 'OVERDUE' | 'CANCELLED';
  description?: string;
  apartmentId: string;
  apartment: Apartment;
  userId: string;
  user: User;
  createdAt: string;
  updatedAt: string;
}

export interface PaginationMeta {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface PaginatedResponse<T> {
  data: T[];
  meta: PaginationMeta;
}

export interface PaginationParams {
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  registrationOnly?: boolean;
} 

export interface EmailNotification {
  id: string;
  subject: string;
  content: string;
  buildingId: string;
  building: Building;
  apartmentGroupId?: string;
  apartmentGroup?: ApartmentGroup;
  sendToAll: boolean;
  wasSentToAll?: boolean;
  attachments: EmailAttachment[];
  status: 'DRAFT' | 'SENDING' | 'SENT' | 'FAILED';
  sentCount: number;
  totalRecipients: number;
  sentAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface EmailAttachment {
  id: string;
  filename: string;
  originalName: string;
  mimeType: string;
  size: number;
  url: string;
}

export interface ApartmentGroup {
  id: string;
  name: string;
  description?: string;
  buildingId: string;
  building: Building;
  apartments: Apartment[];
  createdAt: string;
  updatedAt: string;
}

export interface SendEmailRequest {
  subject: string;
  content: string;
  buildingId: string;
  apartmentGroupId?: string;
  apartmentGroupIds?: string[];
  apartmentIds?: string[];
  sendToAll: boolean;
  deliveryMethods?: string[]; // ['EMAIL', 'APP']
  recipientType?: string; // 'OWNER', 'CO_OWNER', 'MEMBER', 'TENANT', 'AUTHORIZED'
  titleImage?: string; // URL for app notification image
  attachments?: File[];
} 

// Task Management Types
export enum TaskStatus {
  PENDING = 'PENDING',
  IN_PROGRESS = 'IN_PROGRESS', 
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED'
}

export interface Task {
  id: string;
  title: string;
  description: string;
  assignedToId: string;
  assignedTo: User;
  createdById: string;
  createdBy: User;
  dueDate: string;
  status: TaskStatus;
  completedAt?: string;
  attachments: TaskAttachment[];
  updates: TaskUpdate[];
  createdAt: string;
  updatedAt: string;
}

export interface TaskAttachment {
  id: string;
  fileName: string;
  filePath: string;
  fileSize: number;
  mimeType: string;
  updateId?: string;
  createdAt: string;
}

export interface TaskUpdate {
  id: string;
  taskId: string;
  content: string;
  createdById: string;
  createdBy: User;
  isCompleted: boolean;
  createdAt: string;
  attachments?: TaskAttachment[];
}

export interface CreateTaskRequest {
  title: string;
  description: string;
  assignedToId: string;
  dueDate: string;
  priority?: 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT';
}

export interface UpdateTaskRequest {
  content: string;
  markCompleted?: boolean;
  priority?: 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT';
  status?: TaskStatus;
}

// Building Utility Types
export enum UtilityType {
  NO_BOOKING = 'NO_BOOKING',
  TIME_BASED = 'TIME_BASED', 
  FIXED_SLOTS = 'FIXED_SLOTS',
  NOTIFICATION_ONLY = 'NOTIFICATION_ONLY',
}

export enum UtilityGroupStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE'
}

export enum BookingStatus {
  PENDING_CONFIRMATION = 'PENDING_CONFIRMATION',
  CONFIRMED = 'CONFIRMED',
  DEPOSIT_COLLECTED = 'DEPOSIT_COLLECTED',
  IN_PROGRESS = 'IN_PROGRESS',
  DEPOSIT_REFUNDED = 'DEPOSIT_REFUNDED',
  AWAITING_COMPLETION = 'AWAITING_COMPLETION',
  COMPLETED = 'COMPLETED',
  PENDING_CANCELLATION = 'PENDING_CANCELLATION',
  CANCELLED = 'CANCELLED',
}

export interface BuildingUtilityGroup {
  depositRevenueCode?: string | null;
  depositExpenditureCode?: string | null;
  serviceFeeRevenueCode?: string | null;
  id: string;
  name: string;
  nameVi?: string;
  nameEn?: string;
  description?: string;
  icon?: string;
  utilityType: UtilityType;
  displayOrder: number;
  status: UtilityGroupStatus;
  pricing?: any; // JSON object
  usageLimit?: any; // JSON object
  buildingId: string;
  building?: Building;
  utilities?: BuildingUtility[];
  createdAt: string;
  updatedAt: string;
}

// Supporting interfaces for BuildingUtility
export interface ServiceHour {
  days: number[]; // Array of day numbers (1=Monday, 7=Sunday)
  startTime: string;
  endTime: string;
  maxCapacity?: number; // Maximum capacity for this service hour (for FIXED_SLOTS)
  pricingTiers?: PricingTier[]; // Pricing tiers specific to this service hour
}

export interface PricingTier {
  slotThreshold: number; // Number of slots for this tier
  pricePerSlot: number; // Price per slot in VND
  description?: string; // Optional description for this tier
}

export interface AdditionalFee {
  name: string;
  amount: string;
  collectWhen: 'BOOKING' | 'AFTER_USE';
}

export interface CancellationPolicy {
  hoursBeforeStart: string;
  refundType: 'PERCENTAGE' | 'FIXED';
  refundValue: string;
}

export interface TimeSlot {
  startTime: string;
  endTime: string;
}

export interface BuildingUtility {
  id: string;
  name: string;
  description?: string;
  image?: string;
  isActive: boolean;
  location?: string;
  amenities: string[];
  startDate?: Date;
  endDate?: Date;
  serviceHours?: ServiceHour[]; // Now strongly typed with capacity in each service hour
  advanceBookingDays?: any;
  staffAdvanceBookingDays?: { min?: number; max?: number };
  usageLimitPerWeek?: number;
  usageLimitPerMonth?: number;
  maxUsageTimePerWeek?: number;
  maxUsageTimePerMonth?: number;
  maxSlotsPerBooking?: number;
  pricingTiers?: PricingTier[];
  additionalFees?: any;
  cancellationPolicies?: any;
  timeSlots?: any;
  pricing?: any;
  availableSlots?: any;
  depositAmount?: number; // Tiền đặt cọc - Deposit amount for booking
  buildingId: string;
  groupId: string;
  createdAt: Date;
  updatedAt: Date;
  group?: {
    id: string;
    name: string;
    nameEn?: string;
    utilityType: UtilityType;
    icon?: string;
  };
}

export interface BuildingUtilityBooking {
  id: string;
  apartmentId: string
  apartment?: Apartment;
  apartmentMemberId?: string;
  apartmentMember?: {
    id: string;
    fullName: string;
    phone?: string;
    email?: string;
    role: string;
  };
  utilityId: string;
  utility?: BuildingUtility;
  groupId: string;
  group?: BuildingUtilityGroup;
  buildingId: string;
  building?: Building;
  bookingDate: string;
  bookingEndDate?: string | null;
  startTime?: string;
  endTime?: string;
  status: BookingStatus;
  participants?: number;
  numberOfSlots?: number;       // Number of slots/quantity booked
  totalCost?: number;
  depositAmount?: number;      // Tiền đặt cọc - Deposit amount paid
  depositPaid?: boolean;
  depositRefunded?: boolean;   // Whether deposit has been refunded
  depositCollectLedgerEntryId?: string | null;
  depositRefundLedgerEntryId?: string | null;
  additionalFees?: number;     // Phụ phí phát sinh sau khi sử dụng
  finalAmount?: number;        // Số tiền cuối cùng (sau khi trừ cọc, cộng phụ phí)
  notes?: string;
  adminNotes?: string;         // Admin notes for management
  createdById: string;
  createdBy?: User;
  confirmedById?: string;      // User who confirmed the booking (admin)
  confirmedBy?: User;
  confirmedAt?: string;        // When the booking was confirmed
  usedAt?: string;             // When the utility was actually used
  completedAt?: string;        // When the booking was completed
  createdAt: string;
  updatedAt: string;
}

export interface CreateUtilityGroupRequest {
  name: string;
  nameEn?: string;
  description?: string;
  icon?: string;
  utilityType: UtilityType;
  displayOrder: number;
  status: UtilityGroupStatus;
  pricing?: any;
  usageLimit?: any;
  buildingId: string;
  depositRevenueCode?: string;
  depositExpenditureCode?: string;
  serviceFeeRevenueCode?: string;
}

export interface UpdateUtilityGroupRequest {
  name?: string;
  nameEn?: string;
  description?: string;
  icon?: string;
  utilityType?: UtilityType;
  displayOrder?: number;
  status?: UtilityGroupStatus;
  pricing?: any;
  usageLimit?: any;
  depositRevenueCode?: string;
  depositExpenditureCode?: string;
  serviceFeeRevenueCode?: string;
}

export interface PricingPreview {
  slots: number;
  totalCost: number;
  pricePerSlot: number;
}